import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import SunCalc from 'suncalc';

const ThemeContext = createContext(null);
const ThemeDispatch = createContext(null);

function getSystemPreference() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getThemeFromSun(lat, lon) {
  const now = new Date();
  const times = SunCalc.getTimes(now, lat, lon);
  return (now >= times.sunrise && now <= times.sunset) ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem('themeMode') || 'auto'); // 'dark' | 'light' | 'auto'
  const [effectiveTheme, setEffectiveTheme] = useState('dark');
  const intervalRef = useRef(null);

  function computeEffective(currentMode, coords) {
    if (currentMode === 'dark') return 'dark';
    if (currentMode === 'light') return 'light';
    // auto
    if (coords) return getThemeFromSun(coords.latitude, coords.longitude);
    return getSystemPreference();
  }

  useEffect(() => {
    let coords = null;

    function applyTheme() {
      const theme = computeEffective(mode, coords);
      setEffectiveTheme(theme);
      document.documentElement.setAttribute('data-theme', theme);
    }

    if (mode === 'auto') {
      // Try geolocation first
      navigator.geolocation?.getCurrentPosition(
        pos => { coords = pos.coords; applyTheme(); },
        () => applyTheme() // fallback to system preference
      );
      // Also listen to system changes
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', applyTheme);
      // Check every minute for sunrise/sunset crossings
      intervalRef.current = setInterval(applyTheme, 60000);
      applyTheme();
      return () => {
        mq.removeEventListener('change', applyTheme);
        clearInterval(intervalRef.current);
      };
    } else {
      applyTheme();
    }
  }, [mode]);

  function setTheme(newMode) {
    localStorage.setItem('themeMode', newMode);
    setMode(newMode);
  }

  return (
    <ThemeContext.Provider value={{ mode, effectiveTheme }}>
      <ThemeDispatch.Provider value={setTheme}>
        {children}
      </ThemeDispatch.Provider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
export const useSetTheme = () => useContext(ThemeDispatch);
